import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { Button, Select } from 'antd';
import { DollarOutlined, PlusOutlined, CreditCardOutlined } from '@ant-design/icons';
import paymentsActions from 'redux/paymentsSpa/actions';
import { getCurrencySymbol } from 'utils/currencyUtil';
import Can from 'config/Can';
import { CopyToClipboardIcon } from 'components/Shared/Icons';
import useTranslation from 'utils/hooks/useTranslation';
import ConfirmTransferModal from './ConfirmTransferModal';
import AddCardModal from './AddCardModal';

import styles from './style.module.scss';

const { Option } = Select;

const PRESETS = ['£100', '£250', '£500'];

const cardLabel = card => `${card.paymentCardType} •••• ${(card.cardNumber || '').slice(-4)}`;

const AddFunds = ({
  accountDetails = {
    holder: 'PayByPhone Ltd',
    accountNumber: '6754234',
    sortCode: '12-43-54',
    reference: 'GROUPE-VERDIER-01',
  },
}) => {
  const { translateText } = useTranslation();
  const dispatch = useDispatch();
  const paymentsSpa = useSelector(state => state.paymentsSpa);
  const paymentMethods = useMemo(() => paymentsSpa?.data || [], [paymentsSpa]);
  const cardsLoading = paymentsSpa?.loading;
  const cardsError = paymentsSpa?.getError;
  const { currentCorporation } = useSelector(state => state.user);
  const symbol = getCurrencySymbol(currentCorporation?.country);

  const [tab, setTab] = useState('bank');
  const [amount, setAmount] = useState('£250');
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [bankDetailsOpen, setBankDetailsOpen] = useState(false);
  const [confirmTransferModalOpen, setConfirmTransferModalOpen] = useState(false);
  const [addCardModalOpen, setAddCardModalOpen] = useState(false);
  const [paySelectOpen, setPaySelectOpen] = useState(false);

  useEffect(() => {
    dispatch({ type: paymentsActions.GET });
  }, [dispatch]);

  useEffect(() => {
    if (!selectedCardId && paymentMethods.length) {
      setSelectedCardId(paymentMethods[0].id);
    }
  }, [paymentMethods, selectedCardId]);

  let cardPlaceholder;
  if (cardsError) {
    cardPlaceholder = translateText('corporateAccounts.wallet.dashboard.addFunds.cardsError');
  } else if (cardsLoading) {
    cardPlaceholder = translateText('corporateAccounts.wallet.dashboard.addFunds.loadingCards');
  } else {
    cardPlaceholder = translateText('corporateAccounts.wallet.dashboard.addFunds.selectCard');
  }

  return (
    <div className={styles.card} data-testid="walletAddFunds">
      <div className={styles.cardHeader}>
        <span className={`${styles.cardHeaderIcon} ${styles.cardHeaderIconAccent}`}>
          <DollarOutlined />
        </span>
        {translateText('corporateAccounts.wallet.dashboard.addFunds.title')}
      </div>
      <div className={styles.cardBody}>
        <div className={styles.cardCopy}>
          {translateText('corporateAccounts.wallet.dashboard.addFunds.copy')}
        </div>

        <div className={styles.tabs}>
          <button
            type="button"
            className={classNames(styles.tab, { [styles.tabActive]: tab === 'bank' })}
            onClick={() => setTab('bank')}
            data-testid="walletAddFundsTabBank"
          >
            {translateText('corporateAccounts.wallet.dashboard.addFunds.tabBank')}
          </button>
          <button
            type="button"
            className={classNames(styles.tab, { [styles.tabActive]: tab === 'card' })}
            onClick={() => setTab('card')}
            data-testid="walletAddFundsTabCard"
          >
            {translateText('corporateAccounts.wallet.dashboard.addFunds.tabCard')}
          </button>
        </div>

        {tab === 'card' ? (
          <>
            <div className={styles.amountRow}>
              {PRESETS.map(preset => (
                <button
                  type="button"
                  key={preset}
                  className={classNames(styles.amountPreset, {
                    [styles.selected]: amount === preset,
                  })}
                  onClick={() => setAmount(preset)}
                >
                  {preset}
                </button>
              ))}
              <input
                className={styles.amountOther}
                placeholder={translateText(
                  'corporateAccounts.wallet.dashboard.lowBalanceAlert.otherPlaceholder',
                  { symbol },
                )}
                aria-label={translateText(
                  'corporateAccounts.wallet.dashboard.lowBalanceAlert.otherPlaceholder',
                  { symbol },
                )}
              />
            </div>

            <div className={styles.fieldLabel}>
              {translateText('corporateAccounts.wallet.dashboard.addFunds.payWith')}
            </div>
            <div className={styles.paySelectWrap} data-testid="walletAddFundsPaySelect">
              <Select
                className={styles.paySelect}
                popupClassName={styles.payDropdown}
                optionLabelProp="label"
                value={selectedCardId || undefined}
                placeholder={cardPlaceholder}
                loading={Boolean(cardsLoading)}
                onChange={setSelectedCardId}
                getPopupContainer={trigger => trigger.parentNode}
                virtual={false}
                open={paySelectOpen}
                onDropdownVisibleChange={setPaySelectOpen}
                // The "Add a new card" action (below, via dropdownRender) already covers
                // the no-saved-cards case. `notFoundContent={null}` would suppress the
                // whole popup (including that action) when there are zero cards — use an
                // empty-but-truthy node instead so only the "No data" text disappears.
                notFoundContent={<span style={{ display: 'none' }} />}
                dropdownRender={menu => (
                  <>
                    {menu}
                    <Can I="create" a="PaymentSpa" passThrough>
                      {can => (
                        <button
                          type="button"
                          className={styles.payUseNewCard}
                          disabled={!can}
                          data-testid="walletAddFundsUseNewCard"
                          onMouseDown={event => event.preventDefault()}
                          onClick={() => {
                            setPaySelectOpen(false);
                            setAddCardModalOpen(true);
                          }}
                        >
                          {translateText('corporateAccounts.wallet.dashboard.addFunds.addNewCard')}
                        </button>
                      )}
                    </Can>
                  </>
                )}
              >
                {paymentMethods.map(card => (
                  <Option
                    key={card.id}
                    value={card.id}
                    title={cardLabel(card)}
                    label={
                      <span className={styles.payTriggerLabel}>
                        <CreditCardOutlined />
                        {cardLabel(card)}
                      </span>
                    }
                  >
                    {cardLabel(card)}
                  </Option>
                ))}
              </Select>
            </div>

            <div className={styles.addCtaWrap}>
              <Can I="create" a="PaymentSpa" passThrough>
                {can => (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    className={styles.addCta}
                    disabled={!can}
                    data-testid="walletAddFundsCta"
                  >
                    {translateText('corporateAccounts.wallet.dashboard.addFunds.cta', { amount })}
                  </Button>
                )}
              </Can>
            </div>
          </>
        ) : (
          <div data-testid="walletAddFundsBank">
            <div className={styles.bankCopy}>
              {translateText('corporateAccounts.wallet.dashboard.addFunds.bankCopy')}
            </div>

            <div className={styles.bankDetails}>
              <div
                className={styles.bankDetailsToggle}
                onClick={() => setBankDetailsOpen(open => !open)}
                data-testid="walletAddFundsBankDetailsToggle"
              >
                <span className={styles.bankDetailsToggleTitle}>
                  {translateText('corporateAccounts.wallet.dashboard.addFunds.bankDetails.title')}
                </span>
                <span className={styles.bankDetailsToggleAction}>
                  {translateText(
                    bankDetailsOpen
                      ? 'corporateAccounts.wallet.dashboard.addFunds.bankDetails.hide'
                      : 'corporateAccounts.wallet.dashboard.addFunds.bankDetails.show',
                  )}
                </span>
              </div>
              {bankDetailsOpen && (
                <div className={styles.bankDetailsGrid} data-testid="walletAddFundsBankDetails">
                  {[
                    ['accountHolder', 'holder'],
                    ['accountNumber', 'accountNumber'],
                    ['sortCode', 'sortCode'],
                    ['reference', 'reference'],
                  ].map(([labelKey, field]) => (
                    <React.Fragment key={field}>
                      <span className={styles.bankDetailLabel}>
                        {translateText(
                          `corporateAccounts.wallet.dashboard.addFunds.bankDetails.${labelKey}`,
                        )}
                      </span>
                      <span className={styles.bankDetailValueWrap}>
                        <span className={styles.bankDetailValue}>{accountDetails[field]}</span>
                        <a
                          className={styles.bankDetailCopyIcon}
                          onClick={() => navigator.clipboard.writeText(accountDetails[field])}
                          data-testid={`walletAddFundsCopy-${field}`}
                        >
                          <CopyToClipboardIcon />
                        </a>
                      </span>
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.addCtaWrap}>
              <Can I="create" a="PaymentSpa" passThrough>
                {can => (
                  <Button
                    type="primary"
                    className={styles.addCta}
                    disabled={!can}
                    data-testid="walletAddFundsConfirm"
                    onClick={() => setConfirmTransferModalOpen(true)}
                  >
                    {translateText(
                      'corporateAccounts.wallet.dashboard.addFunds.bankDetails.confirm',
                    )}
                  </Button>
                )}
              </Can>
            </div>
          </div>
        )}
      </div>
      <ConfirmTransferModal
        visible={confirmTransferModalOpen}
        onClose={() => setConfirmTransferModalOpen(false)}
        currencySymbol={symbol}
      />
      <AddCardModal visible={addCardModalOpen} onClose={() => setAddCardModalOpen(false)} />
    </div>
  );
};

export default AddFunds;
